<script setup lang="ts">
import type { ComponentStyles } from 'types/index';
import type { MailerLiteFormEmbedContent } from 'types/blocks';

const props = defineProps<{
  componentStyles: ComponentStyles;
  data: MailerLiteFormEmbedContent;
}>();

const { settings } = useRoot();
const embedContainerElement = ref<HTMLElement | null>(null);
const isFormReady = ref(false);

const MAILERLITE_SCRIPT_URL = 'https://assets.mailerlite.com/js/universal.js';
const mailerLiteAccountId = settings.content.mailerLiteAccountId;

const dom = useDom();

type MailerLiteJsonpRequest = {
  make: (path: string, callbackName: string, params?: Record<string, unknown>) => void;
};

type MailerLiteCallbacks = {
  account: () => string | undefined;
  jsonpRequest?: MailerLiteJsonpRequest;
};

type MailerLiteFacade = {
  (...args: unknown[]): void;
  q?: unknown[][];
  fn?: MailerLiteCallbacks;
};

function isMailerLiteInitialized(): boolean {
  if (!dom?.window) return false;

  const windowObject = dom.window as typeof dom.window & { __ml__initialized?: boolean };
  return Boolean(windowObject.__ml__initialized);
}

async function ensureMailerLiteScriptExists(): Promise<void> {
  if (!dom?.window || !mailerLiteAccountId) return;

  const windowObject = dom.window as typeof dom.window & { ml?: MailerLiteFacade };

  if (typeof windowObject.ml === 'function') {
    windowObject.ml('account', mailerLiteAccountId);
    return;
  }

  const queuedCalls: unknown[][] = [];
  const facade: MailerLiteFacade = (...args: unknown[]) => {
    queuedCalls.push(args);
  };

  facade.q = queuedCalls;
  windowObject.ml = facade;

  windowObject.ml('account', mailerLiteAccountId);

  const { load: loadScript } = useScriptTag(MAILERLITE_SCRIPT_URL, undefined, {
    attrs: { id: 'mailerLiteUniversalScript' }
  });

  await loadScript();
}

function requestManualEmbed(): void {
  if (!dom?.window || !mailerLiteAccountId || !props.data.formId) return;

  const windowObject = dom.window as typeof dom.window & { ml?: MailerLiteFacade };
  const facade = windowObject.ml;
  const callbacks = facade?.fn;

  const resolvedAccountId = callbacks?.account?.();
  const jsonpRequest = callbacks?.jsonpRequest;

  if (!resolvedAccountId || !jsonpRequest) {
    void ensureMailerLiteScriptExists();
    return;
  }

  jsonpRequest.make(`/jsonp/${resolvedAccountId}/forms/${props.data.formId}`, 'renderEmbeddedForm');
}

function observeForRenderedContent() {
  if (!embedContainerElement.value) return;

  if (embedContainerElement.value.childElementCount > 0) {
    isFormReady.value = true;
    return;
  }

  const observer = new MutationObserver(() => {
    if (embedContainerElement.value?.childElementCount) {
      isFormReady.value = true;
      observer.disconnect();
    }
  });

  observer.observe(embedContainerElement.value, { childList: true });
}

async function initializeMailerLiteEmbed(): Promise<void> {
  if (!props.data.formId || !mailerLiteAccountId) return;
  if (!dom?.window || !embedContainerElement.value) return;

  observeForRenderedContent();

  if (!isMailerLiteInitialized()) {
    await ensureMailerLiteScriptExists();
    return;
  }

  requestManualEmbed();
}

onMounted(() => {
  void initializeMailerLiteEmbed();
});
</script>

<template>
  <BaseComponentWrapper
    v-if="data.formId && settings.content.mailerLiteAccountId"
    :component-styles="componentStyles"
  >
    <BaseLoader v-if="!isFormReady" />
    <div
      v-show="isFormReady"
      ref="embedContainerElement"
      class="ml-embedded form"
      :data-form="data.formId"
    ></div>
  </BaseComponentWrapper>
</template>
